import { useState, useCallback, useRef, useEffect } from "react";
import type { ProjectionTableRow } from "../types/projection";
import { formatAccountingCurrency, formatCurrency } from "../utils/formatting";
import { cn } from "../utils/cn";

interface ProjectionTableProps {
  rows: ProjectionTableRow[];
  currency: string;
}

/**
 * Render a single cell using accounting-style formatting:
 * negative values displayed as red "(amount)".
 */
function AccountingCell({
  value,
  currency,
  className = "",
}: {
  value: number;
  currency: string;
  className?: string;
}) {
  const { text, isNegative } = formatAccountingCurrency(value, currency);
  return (
    <td
      className={cn(
        "table-cell",
        isNegative && "table-cell-negative",
        className
      )}
    >
      {text}
    </td>
  );
}

function PhaseBadge({ phase }: { phase: "working" | "retired" }) {
  if (phase === "working") {
    return <span className="phase-badge-working-combined">W</span>;
  }
  return <span className="phase-badge-retired-combined">R</span>;
}

/** SVG star icon for the retirement transition row (RWP-C3). */
function RetirementStarIcon() {
  return (
    <svg
      className="retirement-star-icon"
      viewBox="0 0 20 20"
      fill="currentColor"
      aria-hidden="true"
    >
      <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
    </svg>
  );
}

/** Search magnifying-glass icon for the jump-to-age input (RWP-A1). */
function SearchIcon() {
  return (
    <svg
      className="jump-to-age-icon"
      viewBox="0 0 20 20"
      fill="currentColor"
      aria-hidden="true"
    >
      <path
        fillRule="evenodd"
        d="M8 4a4 4 0 100 8 4 4 0 000-8zM2 8a6 6 0 1110.89 3.476l4.817 4.817a1 1 0 01-1.414 1.414l-4.816-4.816A6 6 0 012 8z"
        clipRule="evenodd"
      />
    </svg>
  );
}

/**
 * Detect the first retirement year to highlight the transition row.
 */
function findRetirementTransitionIndex(rows: ProjectionTableRow[]): number {
  return rows.findIndex((r) => r.phase === "retired");
}

/** Check if a row is at a decade boundary (age ends in 0 and is not the first row). */
function isDecadeBoundary(row: ProjectionTableRow, index: number): boolean {
  return index > 0 && row.age % 10 === 0;
}

/** Total column count for full-width spanning rows. */
const TOTAL_COLUMNS = 12;

/** Check if all financial values in every row are zero (empty state). */
function isAllZeroData(rows: ProjectionTableRow[]): boolean {
  return (
    rows.length > 0 &&
    rows.every(
      (r) =>
        r.liquidAssets === 0 &&
        r.liquidAssetsGrowth === 0 &&
        r.appreciableAssets === 0 &&
        r.appreciableAssetsGrowth === 0 &&
        r.depreciatingAssets === 0 &&
        r.depreciatingAssetsLoss === 0 &&
        r.annualContribution === 0 &&
        r.totalDebt === 0 &&
        r.annualDebtPaydown === 0 &&
        r.emergencyFund === 0 &&
        r.netWorth === 0
    )
  );
}

export function ProjectionTable({ rows, currency }: ProjectionTableProps) {
  const retirementIndex = findRetirementTransitionIndex(rows);
  const tableRef = useRef<HTMLDivElement>(null);

  // Jump-to-age state (WP-1 / RWP-A)
  const [jumpAge, setJumpAge] = useState("");
  const [jumpFeedback, setJumpFeedback] = useState<string | null>(null);
  const [highlightedRowYear, setHighlightedRowYear] = useState<number | null>(
    null
  );
  const jumpInputRef = useRef<HTMLInputElement>(null);

  // Clear feedback after 3 seconds (RWP-A2)
  useEffect(() => {
    if (!jumpFeedback) return;
    const timer = setTimeout(() => setJumpFeedback(null), 3000);
    return () => clearTimeout(timer);
  }, [jumpFeedback]);

  // Keyboard shortcut: "/" focuses the jump input (RWP-A5)
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (
        e.key === "/" &&
        !(e.target instanceof HTMLInputElement) &&
        !(e.target instanceof HTMLTextAreaElement)
      ) {
        e.preventDefault();
        jumpInputRef.current?.focus();
      }
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, []);

  const handleJumpToAge = useCallback(() => {
    const targetAge = parseInt(jumpAge, 10);
    if (isNaN(targetAge) || targetAge < 18 || targetAge > 100) return;

    // Find exact or nearest row
    let bestIndex = 0;
    let bestDiff = Infinity;
    for (let i = 0; i < rows.length; i++) {
      const diff = Math.abs(rows[i].age - targetAge);
      if (diff < bestDiff) {
        bestDiff = diff;
        bestIndex = i;
      }
    }

    const matchedAge = rows[bestIndex].age;
    if (matchedAge !== targetAge) {
      setJumpFeedback(`Showing nearest: age ${matchedAge}`);
    } else {
      setJumpFeedback(null);
    }

    // Scroll target row into view
    const rowEl = tableRef.current?.querySelector(
      `[data-row-year="${rows[bestIndex].year}"]`
    );
    if (rowEl) {
      rowEl.scrollIntoView({ behavior: "smooth", block: "center" });
      setHighlightedRowYear(rows[bestIndex].year);
      // Clear highlight after animation
      setTimeout(() => setHighlightedRowYear(null), 1500);
    }
  }, [jumpAge, rows]);

  const handleJumpKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === "Enter") handleJumpToAge();
    },
    [handleJumpToAge]
  );

  // Debounce: auto-trigger jump after 1s of inactivity
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => {
    if (!jumpAge) return;
    debounceRef.current = setTimeout(() => handleJumpToAge(), 1000);
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [jumpAge, handleJumpToAge]);

  // Build flattened rows with decade separators interleaved
  const renderRows: Array<
    | { type: "data"; row: ProjectionTableRow; index: number }
    | { type: "decade"; label: string; key: string }
    | { type: "ribbon"; row: ProjectionTableRow }
  > = [];

  rows.forEach((row, index) => {
    // Insert decade separator before rows where age is a multiple of 10
    if (isDecadeBoundary(row, index)) {
      renderRows.push({
        type: "decade",
        label: `${row.age}s`,
        key: `decade-${row.age}`,
      });
    }

    renderRows.push({ type: "data", row, index });

    // Insert retirement ribbon after the retirement transition row (RWP-C2)
    if (index === retirementIndex) {
      renderRows.push({ type: "ribbon", row });
    }
  });

  const allZero = isAllZeroData(rows);

  return (
    <div>
      {/* Toolbar: title + jump-to-age search (RWP-A4) */}
      <div className="projection-toolbar" id="projection-toolbar">
        <h3 className="projection-toolbar-title">Year-by-Year Projections</h3>
        <div className="projection-toolbar-actions">
          <div className="jump-to-age-container">
            <SearchIcon />
            <input
              ref={jumpInputRef}
              type="number"
              className="jump-to-age-input"
              placeholder="Age..."
              min={18}
              max={100}
              value={jumpAge}
              onChange={(e) => setJumpAge(e.target.value)}
              onKeyDown={handleJumpKeyDown}
              aria-label="Jump to age"
            />
            <span
              className="jump-to-age-shortcut-badge"
              title="Press / to search"
            >
              /
            </span>
          </div>
          {jumpFeedback && (
            <span className="jump-to-age-feedback">{jumpFeedback}</span>
          )}
        </div>
      </div>

      {/* Empty state guidance */}
      {allZero && (
        <div className="projection-empty-state" id="projection-empty-state">
          <span aria-hidden="true">📊</span>
          <p>
            All projections show $0 — add your financial details in Settings to
            see personalized projections.
          </p>
        </div>
      )}

      {/* Scroll hint */}
      <div className="projection-scroll-hint" aria-hidden="true">
        <span>← Scroll for more →</span>
      </div>

      {/* Scrollable table container (RWP-B1) */}
      <div
        className="projection-table-container"
        id="projection-table"
        ref={tableRef}
      >
        <table className="projection-table-base">
          <thead className="table-header-row projection-table-sticky-header">
            {/* Top-level column group headers */}
            <tr>
              <th className="table-header-age" rowSpan={2}>
                Year (Age)
              </th>
              <th className="table-header-portfolio" colSpan={3}>
                Liquid Assets
              </th>
              <th className="table-header-rrsp" colSpan={2}>
                Property
              </th>
              <th className="table-header-tfsa" colSpan={2}>
                Vehicles
              </th>
              <th className="table-header-nonreg" colSpan={2}>
                Liabilities
              </th>
              <th className="table-header-income" rowSpan={2}>
                <div>Emergency Fund</div>
                <div className="table-header-subscript">⑩</div>
              </th>
              <th className="table-header-surplus" rowSpan={2}>
                <div>Net Worth</div>
                <div className="table-header-subscript-emerald">①+④+⑥−⑧+⑩</div>
              </th>
            </tr>
            {/* Sub-headers with circled numbers for source columns */}
            <tr className="table-subheader-row">
              <th className="table-cell-right-blue">Balance ①</th>
              <th className="table-cell-right">Growth ②</th>
              <th className="table-cell-right">Contributions ③</th>
              <th className="table-cell-right-indigo">Value ④</th>
              <th className="table-cell-right">Gain ⑤</th>
              <th className="table-cell-right-purple">Value ⑥</th>
              <th className="table-cell-right">Loss ⑦</th>
              <th className="table-cell-right-red">Balance ⑧</th>
              <th className="table-cell-right">Paydown ⑨</th>
            </tr>
          </thead>
          <tbody className="table-body">
            {renderRows.map((entry) => {
              if (entry.type === "decade") {
                return (
                  <tr
                    key={entry.key}
                    className="projection-table-decade-row"
                    aria-hidden="true"
                  >
                    <td colSpan={TOTAL_COLUMNS}>{entry.label}</td>
                  </tr>
                );
              }

              if (entry.type === "ribbon") {
                return (
                  <tr
                    key={`ribbon-${entry.row.year}`}
                    className="projection-retirement-ribbon"
                  >
                    <td colSpan={TOTAL_COLUMNS}>
                      <span className="projection-retirement-ribbon-text">
                        🎉 Retirement begins! Starting with{" "}
                        {formatCurrency(entry.row.netWorth, currency)} net
                        worth.
                      </span>
                    </td>
                  </tr>
                );
              }

              const { row, index } = entry;
              const isRetirementTransition = index === retirementIndex;
              const isEvenRow = index % 2 === 0;
              const zebraClass = isEvenRow
                ? "table-row-zebra-even"
                : "table-row-zebra-odd";
              const isHighlighted = highlightedRowYear === row.year;

              const rowClass = isRetirementTransition
                ? "projection-row-retirement"
                : "table-row-normal";

              return (
                <tr
                  key={row.year}
                  data-row-year={row.year}
                  className={cn(
                    rowClass,
                    !isRetirementTransition && zebraClass,
                    isHighlighted && "projection-row-highlight"
                  )}
                  aria-label={
                    isRetirementTransition
                      ? `Retirement begins at age ${row.age}`
                      : undefined
                  }
                >
                  <td
                    className={cn(
                      "table-row-year",
                      isRetirementTransition
                        ? "table-row-year-transition"
                        : isEvenRow
                          ? "table-row-year-even"
                          : "table-row-year-odd"
                    )}
                  >
                    {isRetirementTransition && <RetirementStarIcon />}
                    {row.year} ({row.age})
                    <PhaseBadge phase={row.phase} />
                  </td>
                  <AccountingCell
                    value={row.liquidAssets}
                    currency={currency}
                    className="table-cell-divider-blue"
                  />
                  <AccountingCell
                    value={row.liquidAssetsGrowth}
                    currency={currency}
                  />
                  <AccountingCell
                    value={row.annualContribution}
                    currency={currency}
                  />
                  <AccountingCell
                    value={row.appreciableAssets}
                    currency={currency}
                    className="table-cell-divider-indigo"
                  />
                  <AccountingCell
                    value={row.appreciableAssetsGrowth}
                    currency={currency}
                  />
                  <AccountingCell
                    value={row.depreciatingAssets}
                    currency={currency}
                    className="table-cell-divider-purple"
                  />
                  <AccountingCell
                    value={row.depreciatingAssetsLoss}
                    currency={currency}
                  />
                  <AccountingCell
                    value={row.totalDebt}
                    currency={currency}
                    className="table-cell-divider-red"
                  />
                  <AccountingCell
                    value={row.annualDebtPaydown}
                    currency={currency}
                  />
                  <AccountingCell
                    value={row.emergencyFund}
                    currency={currency}
                    className="table-cell-divider-amber"
                  />
                  <AccountingCell
                    value={row.netWorth}
                    currency={currency}
                    className="table-cell-surplus"
                  />
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
